import { type FC, useState } from "react";

import { CancelSaveMenu } from "components/menus/CancelSaveMenu";
import { InteractionMode, PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { LegacyButton } from "mds/components/LegacyButton";
import { RadioButton } from "mds/components/RadioButton";
import { Tooltip } from "mds/components/Tooltip";
import { ChevronDownIcon, GearSettingsIcon } from "mds/icons";
import { storeApi } from "store/index";
import { NestedPageGroupType } from "store/selectors";
import { GROUP_INTERACTION_MODES, GroupInteractionModeType } from "utils/page-utils";

interface InteractionModeConfigProps {
  page: PageType;
  pageGroups: NestedPageGroupType[];
  isDisabled?: boolean;
  isReadOnly?: boolean;
  title: string;
}

type GroupInteractionMode = InteractionMode.ASSIGN_STUDENTS | InteractionMode.SELF_ASSIGN;

export const InteractionModeConfig: FC<InteractionModeConfigProps> = ({
  page,
  pageGroups,
  isDisabled,
  isReadOnly,
  title,
}) => {
  const interactionMode = page.interaction_mode as GroupInteractionMode;
  const [currentInteractionMode, setCurrentInteractionMode] =
    useState<GroupInteractionMode>(interactionMode);

  const onSave = () => {
    storeApi.pages.partial_update({ id: page.id, interaction_mode: currentInteractionMode });

    // When switching group mode to "ASSIGN_STUDENTS", mark all staged students
    // (who may be in this state because of student self-select) as ready.
    if (currentInteractionMode === InteractionMode.ASSIGN_STUDENTS) {
      pageGroups.forEach((pageGroup) => {
        pageGroup.page_group_users.forEach((pageGroupUser) => {
          storeApi.page_group_users // TODO: Bulk update
            .partial_update({
              id: pageGroupUser.id,
              is_ready: true,
            });
        });
      });
    }
  };

  return (
    <Tooltip text={isDisabled || isReadOnly ? title : ""}>
      <CancelSaveMenu
        button={
          <LegacyButton
            className="gap-1"
            disabled={isDisabled || isReadOnly}
            kind="secondary"
            size="s"
            title={!isDisabled && !isReadOnly ? title : ""}
          >
            <GearSettingsIcon />
            {t(
              `group_configuration_card.group_type.${
                page.interaction_mode as GroupInteractionMode
              }`,
            )}
            {!isReadOnly && <ChevronDownIcon />}
          </LegacyButton>
        }
        saveEventName="Group selection method updated"
        saveEventProps={{ fieldValue: currentInteractionMode }}
        title={t("group_configuration_card.config.interaction_mode")}
        onCancel={() => setCurrentInteractionMode(interactionMode)}
        onSave={onSave}
      >
        <div className="flex flex-col gap-1">
          {GROUP_INTERACTION_MODES.map((choice) => {
            return (
              <RadioButton
                checked={currentInteractionMode === choice}
                containerClassName="text-black-tint-20 body-s"
                id={choice}
                key={choice}
                name="pageGroupType"
                value={choice}
                onChange={(event) => {
                  setCurrentInteractionMode(event.target.value as GroupInteractionMode);
                }}
              >
                {t(`group_configuration_card.choice.${choice as GroupInteractionModeType}`)}
              </RadioButton>
            );
          })}
        </div>
      </CancelSaveMenu>
    </Tooltip>
  );
};
